import { App, Button } from 'antd';
import type { MenuProps } from 'antd/lib';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AiDocumentChat from '../../components/Ai/AiDocumentChat';
import AiSummary from '../../components/Ai/AiSummary';
import { Icon } from '../../components/Core/Icon';
import FileDetail from '../../components/Files/FileDetail';
import { AiClassificationStatusEnum } from '../../enums/AiClassificationStatusEnum';
import { FileLimitationEnum } from '../../enums/FileLimitationEnum';
import { FileSalesStatusEnum } from '../../enums/FileSalesStatusEnum';
import { FileTypeEnum } from '../../enums/FileTypeEnum';
import { SpecialFolderTypeEnum } from '../../enums/SpecialFolderTypeEnum';
import type File from '../../interfaces/File';
import type FileWithToken from '../../interfaces/FileWithToken';
import type { OfficeConfig } from '../../interfaces/OfficeConfig';
import { CollaboraService } from '../../services/collaboraApiService';
import { FileService } from '../../services/fileApiService';
import { PublicFileService } from '../../services/publicFileApiService';
import { useAppStore } from '../../store/store';
import {
  getPublicDownloadFilename,
  getResponseHeader,
  triggerBlobDownload,
} from '../../lib/fileDownload';
import { mapPublicLinkHttpError } from '../../utils/publicLinkErrors';
import {
  getHashFromBytes,
  getOfficeFileBaseUrl,
  getOfficeFileFavicon,
  hasFileLimitation,
} from '../../utils/utils';
import { useFileDownload } from '../useFileDownload';
import { useFilesActions } from '../useFilesActions';
import useOpenFile from '../useOpenFile';
import { useRightPanel } from '../useRightPanel';
import {
  applyFilemanagerHostPreferences,
  extractHostPreferencesFromMessage,
  OfficePostMessageId,
  parseOfficePostMessageData,
  parseRequestFileActionValues,
  readFilemanagerHostPreferences,
  sendHostFileMetaToEditor,
  sendHostPreferencesToEditor,
} from '../../lib/officeGrantPostMessage/officePreferencesProtocol';
import { appendAiGrantPreferencesToUrl } from '../../lib/aiGrantPreferencesUrl';
import { useRoutePageTitle } from '../../lib/documentTitle';
import {
  buildOfficeGrantOpenUrl,
  getOfficeEditorChoice,
  isOfficeGrantBetaEnabled,
  resolveOfficeEditorChoice,
  setOfficeEditorChoice,
  type OfficeEditorChoice,
} from '../../lib/officeEditorChoice';
import {
  collectOfficeEditorLifecycleOrigins,
  shouldListenForOfficeEditorLifecycle,
} from '../../lib/officeEditorLifecycle/officeEditorLifecycle';
import { isViewerEmbedSession } from '../../utils/viewerWebviewAuth';

interface UseOfficeProps {
  type: FileTypeEnum;
  /** Override route param — used when Office is embedded in the bottom drawer. */
  fileId?: string | number;
  /** Drawer embed: close sheet instead of navigating away on UI_Close / delete. */
  onRequestClose?: () => void;
}

/**
 * Decodes a JWT token and returns its expiration timestamp in milliseconds.
 * If decoding fails or expiration is missing, returns null.
 * @param token JWT token string
 * @returns Expiration time in ms, or null
 */
function getJwtExpiration(token: string): number | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/');
    if (!base64) return null;
    // Decode JWT payload safely and parse
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    const payload = JSON.parse(jsonPayload);

    // 'exp' in JWT is in seconds, convert to ms if present
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Hook to encapsulate Office file viewing, including
 * fetching, token management, iframe creation, and file actions.
 * Supports both MS Office Online and Collabora Online providers.
 */
export function useOffice({ type, fileId: fileIdProp, onRequestClose }: UseOfficeProps) {
  const { t } = useTranslation();
  const { modal, notification } = App.useApp();
  const {
    openRightPanel,
    closeRightPanel,
    setRightPanelContent,
    setRightPanelKind,
    isRightPanelOpen,
  } = useRightPanel();
  // General loading state
  const [isLoading, setIsLoading] = useState(false);
  const [isMoveActionLoading, setIsMoveActionLoading] = useState(false);
  // Office file info with access token
  const [file, setFile] = useState<FileWithToken>();
  // WOPI access token for Office iframe
  const [accessToken, setAccessToken] = useState('');
  // Timestamp (ms) for when token expires
  const [accessTokenExpiry, setAccessTokenExpiry] = useState(0);
  // Complete WOPI source url for iframe
  const [wopiSource, setWopiSource] = useState('');
  // OfficeGrant beta `/open` loads via iframe src (GET), not form POST access_token.
  const [useDirectIframeSrc, setUseDirectIframeSrc] = useState(false);
  // null = beta on and waiting for chooser modal (or public-link force classic).
  const [editorChoice, setEditorChoice] = useState<OfficeEditorChoice | null>(() =>
    resolveOfficeEditorChoice({
      betaEnabled: isOfficeGrantBetaEnabled(),
      storedChoice: getOfficeEditorChoice(),
    }),
  );

  const navigate = useNavigate();
  const { fileId: fileIdFromParams } = useParams();
  const fileId = fileIdProp != null ? String(fileIdProp) : fileIdFromParams;
  const [searchParams] = useSearchParams();
  const hasPublicFileToken = searchParams.get('publicFileToken') !== null;
  const { handleDownloadFiles } = useFileDownload();
  const { redirectFileToLocation } = useOpenFile();
  const {
    handleShareFiles,
    handleDownloadEncryptedFile,
    handleMoveFiles,
    handleRenameFile,
    handleDeleteFiles,
  } = useFilesActions({});

  const fileService = new FileService();
  const publicFileService = new PublicFileService();
  const collaboraService = new CollaboraService();
  const getPublicFilePassword = useAppStore((s) => s.getPublicFilePassword);

  // Office provider config from Zustand store
  const officeConfig = useAppStore((s) => s.officeConfig);
  const setOfficeConfig = useAppStore((s) => s.setOfficeConfig);
  const locale = useAppStore((s) => s.locale);
  const setLocale = useAppStore((s) => s.setLocale);
  const colorSchemePreference = useAppStore((s) => s.colorSchemePreference);
  const setColorSchemePreference = useAppStore((s) => s.setColorSchemePreference);
  const highContrastEnabled = useAppStore((s) => s.highContrastEnabled);
  const setHighContrastEnabled = useAppStore((s) => s.setHighContrastEnabled);
  const reduceMotionEnabled = useAppStore((s) => s.reduceMotionEnabled);
  const setReduceMotionEnabled = useAppStore((s) => s.setReduceMotionEnabled);

  // Ref to track if component is mounted; avoid updating state on unmount
  const mountedRef = useRef(true);
  // Ref to track if token expired modal is already shown
  const tokenExpiredModalRef = useRef<ReturnType<typeof modal.warning> | null>(null);
  const betaChooserShownRef = useRef(false);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  /**
   * OfficeGrant beta chooser: when flag is on and session has no choice, ask once per session.
   * Public links always stay on the classic provider path (no refreshToken handoff).
   */
  useEffect(() => {
    if (editorChoice !== null) return;

    if (hasPublicFileToken || !isOfficeGrantBetaEnabled()) {
      setEditorChoice('classic');
      return;
    }

    if (betaChooserShownRef.current) return;
    betaChooserShownRef.current = true;

    setIsLoading(true);

    modal.confirm({
      title: t('modal.officeEditorBeta.title'),
      content: t('modal.officeEditorBeta.description'),
      closable: false,
      mask: { closable: false },
      centered: true,
      okText: t('modal.officeEditorBeta.tryNew'),
      cancelText: t('modal.officeEditorBeta.useClassic'),
      onOk: () => {
        setOfficeEditorChoice('officegrant');
        setEditorChoice('officegrant');
      },
      onCancel: () => {
        setOfficeEditorChoice('classic');
        setEditorChoice('classic');
      },
    });
  }, [editorChoice, hasPublicFileToken, modal, t]);

  /**
   * Map FileWithToken to File for downstream file actions
   */
  const convertToFile = useCallback((fileWithToken: FileWithToken): File => {
    return {
      id: fileWithToken.id,
      cufi: fileWithToken.cufi,
      fileName: fileWithToken.fileName,
      fileExtension: fileWithToken.fileExtension,
      fileSize: fileWithToken.fileSize,
      isReadOnly: fileWithToken.isReadOnly,
      fileType: fileWithToken.fileType,
      // These default to false for viewer scenario
      isFavourite: false,
      isPasswordBypass: false,
      isArchived: false,
      isDeleted: false,
      isShared: false,
      isUnRead: false,
      isPublicShare: false,
      isShortcut: false,
      isPasswordSet: false,
      lastModifiedAt: 0,
      isFolder: false,
      hasShortcuts: false,
      ownerId: 0,
      tags: [],
      fileLimitations: fileWithToken.fileLimitations,
      thumbnail: '',
      salesStatus: FileSalesStatusEnum.None,
      aiOperationStatus: AiClassificationStatusEnum.None,
      specialFolder: SpecialFolderTypeEnum.None,
    };
  }, []);

  /**
   * A memoized variable that parses the file into a File object.
   * Returns a File object if the file is available, otherwise null.
   * Same logic as in useViewer.
   */
  const parsedFile: File | null = useMemo(() => {
    if (!file) return null;

    return convertToFile(file);
  }, [file, convertToFile]);

  /**
   * A memoized variable that checks if the file can be shared based on the file limitations.
   * Returns true if the file can be shared, otherwise false.
   * Same logic as in useViewer.
   */
  const canShareFile = useMemo(() => {
    if (!file) return false;
    return !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileShare);
  }, [file]);

  /** Same rule as Organize → Move in fileActions (no DenyFileMove). */
  const canMoveFile = useMemo(() => {
    if (!file) return false;
    return !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileMove);
  }, [file]);

  /**
   * Navigate to containing FM folder. Public links have no FM folder.
   * Do not gate on DenyFileOpenFileLocation — BE sets that for non-shortcut
   * "open original" N/A; GlobalSearch openFileLocation also ignores it.
   */
  const canOpenContainingFolder = useMemo(() => {
    return Boolean(file && !hasPublicFileToken);
  }, [file, hasPublicFileToken]);

  const canRenameFile = useMemo(() => {
    if (!file) return false;
    return !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileRename);
  }, [file]);

  const canDeleteFile = useMemo(() => {
    if (!file) return false;
    return !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileDelete);
  }, [file]);

  /** OfficeGrant beta `/open`: File menu owns host actions — hide FloatButton.Group. */
  const hideHostOwnedFloatActions = editorChoice === 'officegrant';

  /**
   * A memoized variable that checks if the file can be downloaded based on the file permissions.
   * Returns true if the file can be downloaded, otherwise false.
   * Same logic as in useViewer.
   */
  const canDownloadFile = useMemo(() => {
    return file?.canDownload;
  }, [file]);

  /**
   * Shares the file using main app's file actions.
   * No-op if file isn't loaded or can't be shared.
   */
  const handleShare = useCallback(() => {
    if (!parsedFile || !canShareFile) return;
    handleShareFiles([parsedFile]);
  }, [parsedFile, canShareFile, handleShareFiles]);

  /**
   * Downloads the file: public download API when guest link, else authenticated flow.
   * No-op if file isn't loaded or can't be downloaded.
   */
  const handleDownload = useCallback(async () => {
    if (!parsedFile || !canDownloadFile) return;

    const publicFileToken = searchParams.get('publicFileToken');
    if (publicFileToken) {
      try {
        const accessCode =
          searchParams.get('publicAccessCode') ||
          getPublicFilePassword(publicFileToken) ||
          undefined;
        const { blob, headers } = await publicFileService.downloadPublicFile(
          publicFileToken,
          parsedFile.id,
          accessCode || undefined,
        );
        const fileName = getPublicDownloadFilename(headers, parsedFile.fileName ?? 'download');
        const contentSha256 = getResponseHeader(headers, 'x-content-sha256');
        if (contentSha256) {
          const calculatedHash = await getHashFromBytes(await blob.arrayBuffer(), false);
          if (contentSha256.trim().toLowerCase() !== calculatedHash.trim().toLowerCase()) {
            notification.error({
              title: t('errors.fileIntegrityCheckFailed') || 'File integrity check failed',
              description:
                t('errors.fileIntegrityCheckFailedDescription') ||
                'The downloaded file may be corrupted. Please try downloading again.',
              duration: 0,
            });
            return;
          }
        }
        triggerBlobDownload(blob, fileName);
      } catch (error: unknown) {
        const mapped = mapPublicLinkHttpError(error);
        if (mapped.kind === 'watermarkDenied') {
          notification.error({
            title: t('errors.watermarkProtectionDownloadDenied'),
          });
          return;
        }
        notification.error({ title: t('errors.genericError') });
      }
      return;
    }

    handleDownloadFiles([parsedFile]);
  }, [
    parsedFile,
    canDownloadFile,
    handleDownloadFiles,
    searchParams,
    getPublicFilePassword,
    notification,
    t,
  ]);

  /**
   * Opens move modal with real ownerId (convertToFile uses ownerId 0; FileMoveForm needs a match).
   */
  const handleOpenMoveModal = useCallback(async () => {
    if (!parsedFile || hasPublicFileToken || !canMoveFile) return;
    setIsMoveActionLoading(true);
    try {
      const detail = await fileService.getFile(parsedFile.id);
      const rawOwnerId = detail.ownerId;
      const ownerId =
        typeof rawOwnerId === 'string' ? parseInt(rawOwnerId, 10) : Number(rawOwnerId);
      if (!Number.isFinite(ownerId)) {
        notification.error({
          title: t('errors.genericFetchError', { resource: t('common.file') }),
        });
        return;
      }
      const fileForMove: File = { ...parsedFile, ownerId };
      handleMoveFiles([fileForMove], undefined, undefined);
    } catch {
      notification.error({
        title: t('errors.genericFetchError', { resource: t('common.file') }),
      });
    } finally {
      setIsMoveActionLoading(false);
    }
  }, [canMoveFile, handleMoveFiles, hasPublicFileToken, notification, parsedFile, t]);

  /**
   * Viewer JWT has no parentId — resolve via /myfiles/specific, then navigate to parent
   * (null/0 parentId → personal/received section root, same as redirectFileToLocation).
   */
  const handleOpenContainingFolder = useCallback(async () => {
    if (!parsedFile || !canOpenContainingFolder) return;
    try {
      const fileRes = await fileService.getSpecificFiles([parsedFile.id]);
      const located = fileRes.myFiles?.[0];
      if (!located) {
        notification.error({
          title: t('errors.genericFetchError', { resource: t('common.file') }),
        });
        return;
      }
      // Coerce non-positive parentId like convert-to-pdf path helpers (section root).
      const parentId = located.parentId != null && located.parentId > 0 ? located.parentId : null;
      redirectFileToLocation({
        file: { ...located, parentId },
        avoidOpenViewer: true,
        queryParams: {
          prependFileId: String(located.id),
        },
      });
    } catch {
      notification.error({
        title: t('errors.genericFetchError', { resource: t('common.file') }),
      });
    }
  }, [canOpenContainingFolder, notification, parsedFile, redirectFileToLocation, t]);

  /**
   * Host rename from Office File menu — posts Host_FileMeta on success so ribbon can update.
   */
  const handleHostRename = useCallback(
    (source: MessageEventSource | null, origin: string) => {
      if (!parsedFile || !canRenameFile || hasPublicFileToken) {
        notification.error({ title: t('errors.forbidden') });
        return;
      }
      handleRenameFile(parsedFile, {
        onFilesUpdated: (updated) => {
          const renamed = updated[0];
          if (!renamed) return;
          setFile((prev) => (prev ? { ...prev, fileName: renamed.fileName } : prev));
          sendHostFileMetaToEditor(source, origin, {
            fileId: renamed.id,
            fileName: renamed.fileName,
          });
        },
      });
    },
    [canRenameFile, handleRenameFile, hasPublicFileToken, notification, parsedFile, t],
  );

  /**
   * Host delete from Office File menu — confirm, soft-delete, leave editor.
   */
  const handleHostDelete = useCallback(() => {
    if (!parsedFile || !canDeleteFile || hasPublicFileToken) {
      notification.error({ title: t('errors.forbidden') });
      return;
    }
    modal.confirm({
      title: t('modal.officeEditorDelete.title'),
      content: t('modal.officeEditorDelete.description'),
      okText: t('common.delete'),
      centered: true,
      onOk: () => {
        handleDeleteFiles([parsedFile], {
          onFilesDeleted: () => {
            if (onRequestClose) {
              onRequestClose();
              return;
            }
            navigate('/files/personal');
          },
        });
      },
    });
  }, [
    canDeleteFile,
    handleDeleteFiles,
    hasPublicFileToken,
    modal,
    navigate,
    notification,
    onRequestClose,
    parsedFile,
    t,
  ]);

  /** Host details from Office File menu — same RightPanel + FileDetail as files list. */
  const handleHostDetails = useCallback(() => {
    if (!parsedFile) return;
    // ponytail: FM Details has no Deny* gate (only single-selection); FileDetail handles lock UI.
    setRightPanelKind('detail');
    setRightPanelContent(<FileDetail file={parsedFile} />);
    openRightPanel();
  }, [openRightPanel, parsedFile, setRightPanelContent, setRightPanelKind]);

  /**
   * Inserts an Office iframe into #frameholder and submits office_form with correct token.
   * Only occurs once; further reruns are prevented.
   */
  const buildOfficeFrame = useCallback(() => {
    // Host div for the iframe
    const frameholder = document.getElementById('frameholder');
    if (!frameholder) return;

    // Create and configure iframe for Office content
    const office_frame = document.createElement('iframe');
    office_frame.name = 'office_frame';
    office_frame.id = 'office_frame';
    office_frame.title = file?.fileName || 'Office Frame';
    office_frame.setAttribute('allowfullscreen', 'true');
    // clipboard-* : editor copy/paste + Next overlay copy (crbug 414348233)
    // fullscreen : Office allowfullscreen
    office_frame.setAttribute('allow', 'clipboard-read; clipboard-write; fullscreen');
    // Network is allowed with scripts+same-origin; sandbox does not block fetch.
    office_frame.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads allow-top-navigation allow-popups-to-escape-sandbox',
    );
    office_frame.className = 'w-full h-full absolute inset-0 border-none block';

    // Beta OfficeGrant `/open`: navigate iframe via GET (auth + prefs already in QS).
    if (useDirectIframeSrc && wopiSource) {
      office_frame.src = wopiSource;
      frameholder.appendChild(office_frame);
      return;
    }

    frameholder.appendChild(office_frame);

    // Submit Office auth token via hidden form (allows POST token to iframe src)
    const officeForm = document.getElementById('office_form') as HTMLFormElement | null;
    officeForm?.submit();
  }, [file?.fileName, useDirectIframeSrc, wopiSource]);

  /**
   * When the fileId, Office type, or params change, retrieve file info,
   * build the correct WOPI src and favicon, and store access token.
   * Supports both MS Office Online and Collabora Online providers.
   */
  useEffect(() => {
    if (!fileId) return;
    // Wait for beta chooser (or public-link → classic) before fetching/launching editor.
    if (editorChoice === null) return;

    let cancelled = false;

    // Read-only/Token/Version params from query string.
    // Webview embed (token+rToken) always forces view-only — including Collabora.
    const isReadOnly = searchParams.get('r') === '1' || isViewerEmbedSession();
    const tokenFromUrl = searchParams.get('acToken') || undefined;
    const fileVersionId = searchParams.get('version') || undefined;
    const publicFileToken = searchParams.get('publicFileToken') || undefined;
    const publicAccessCode = searchParams.get('publicAccessCode') || undefined;

    setIsLoading(true);
    setUseDirectIframeSrc(false);

    /**
     * Handles the response from the file service.
     * Sets file metadata and redirects to regular viewer if not an office type.
     */
    const handleViewerResponse = (response: FileWithToken) => {
      if (cancelled || !mountedRef.current) return;
      // Empty wTkn = no Office editor (file-scoped sessions always send ""). Fall back to viewer.
      if (!response.wTkn) {
        const token = searchParams.get('acToken') || undefined;
        const viewerPath = publicFileToken
          ? `/viewer/${fileId}?publicFileToken=${encodeURIComponent(publicFileToken)}`
          : token
            ? `/viewer/${fileId}?acToken=${encodeURIComponent(token)}`
            : `/viewer/${fileId}`;
        navigate(viewerPath);
        return;
      }

      const isOfficeType = [
        FileTypeEnum.Wopitest,
        FileTypeEnum.Powerpoint,
        FileTypeEnum.Word,
        FileTypeEnum.Excel,
      ].includes(response.fileType ?? FileTypeEnum.Unknown);

      if (
        (!isOfficeType &&
          !hasFileLimitation(response.fileLimitations, FileLimitationEnum.DenyFileAiScraping) &&
          response.isReadOnly) ||
        response.watermark
      ) {
        const viewerPath = publicFileToken
          ? `/viewer/${fileId}?publicFileToken=${encodeURIComponent(publicFileToken)}`
          : `/viewer/${fileId}`;
        navigate(viewerPath);
        return;
      }

      setFile(response);
    };

    /**
     * Sets up the MS Office Online editor (unchanged legacy flow).
     */
    const setupMsOffice = (response: FileWithToken) => {
      if (cancelled || !mountedRef.current) return;

      // Figure out base url and favicon for Office flavor (Word, Excel, etc)
      let baseUrl = '';
      let favIcon = '';
      switch (type) {
        case FileTypeEnum.Word:
          baseUrl = getOfficeFileBaseUrl(FileTypeEnum.Word, isReadOnly);
          favIcon = getOfficeFileFavicon(FileTypeEnum.Word);
          break;
        case FileTypeEnum.Excel:
          baseUrl = getOfficeFileBaseUrl(FileTypeEnum.Excel, isReadOnly);
          favIcon = getOfficeFileFavicon(FileTypeEnum.Excel);
          break;
        case FileTypeEnum.Powerpoint:
          baseUrl = getOfficeFileBaseUrl(FileTypeEnum.Powerpoint, isReadOnly);
          favIcon = getOfficeFileFavicon(FileTypeEnum.Powerpoint);
          break;
        case FileTypeEnum.Wopitest:
          baseUrl = getOfficeFileBaseUrl(FileTypeEnum.Wopitest, isReadOnly);
          favIcon = getOfficeFileFavicon(FileTypeEnum.Wopitest);
          break;
      }

      // Office Online requires full BCP 47 locale (e.g. 'en-US', 'it-IT'), not short codes like 'en'
      const browserLang = navigator.language || 'en-US';
      const shortToFull: Record<string, string> = {
        en: 'en-US',
        it: 'it-IT',
        es: 'es-ES',
        tr: 'tr-TR',
      };
      const officeCulture = browserLang.includes('-')
        ? browserLang
        : (shortToFull[browserLang] ?? 'en-US');

      // Compose WOPI url for Office viewer
      const wopiSrc = `${import.meta.env.VITE_APP_OFFICE_WOPISOURCE_URL}/api/wopi/files/${fileId}`;
      const cacheBuster = Date.now(); // Prevent caching for viewer
      const url =
        `${baseUrl}wopisrc=${encodeURIComponent(wopiSrc)}` +
        `&IsLicensedUser=1&ui=${officeCulture}&rs=${officeCulture}&v=${cacheBuster}`;
      setWopiSource(url);

      // Swap favicon for corresponding office type
      const appIcon: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      if (appIcon && favIcon) appIcon.href = favIcon;

      // Use wTkn from viewer response
      setAccessToken(response.wTkn);
      const exp = getJwtExpiration(response.wTkn);
      if (exp) setAccessTokenExpiry(exp);
    };

    /**
     * Beta path: iframe OfficeGrant `/open` with refreshToken + prefs.
     * Does not call OnlyOffice / OfficeGrant EditorUrl APIs — Office app bootstraps itself.
     */
    const setupOfficeGrantBetaOpen = (officeGrantBaseUrl: string) => {
      if (cancelled || !mountedRef.current) return;

      const prefs = useAppStore.getState();
      const openUrl = buildOfficeGrantOpenUrl({
        officeGrantBaseUrl,
        fileId,
        refreshToken: prefs.customTokens?.rToken,
        isReadOnly,
        preferences: {
          locale: prefs.locale,
          colorSchemePreference: prefs.colorSchemePreference,
          highContrastEnabled: prefs.highContrastEnabled,
          reduceMotionEnabled: prefs.reduceMotionEnabled,
        },
      });

      setUseDirectIframeSrc(true);
      setWopiSource(openUrl);
      // Placeholder so the iframe builder gate passes; OfficeGrant owns real WOPI tokens.
      setAccessToken('officegrant-open');
      setAccessTokenExpiry(Number.MAX_SAFE_INTEGER);
    };

    /**
     * Sets up a WOPI-based editor (Collabora Online, ONLYOFFICE Docs, or OfficeGrant).
     * Shared flow: fetch editor launch data + discovery XML (when needed), then build the editor URL.
     * Only the editor-url endpoint and the server-URL field differ between providers.
     */
    const setupWopiEditor = async (response: FileWithToken, config: OfficeConfig) => {
      if (cancelled || !mountedRef.current) return;

      const isOnlyOffice = config.provider === 'onlyoffice';
      const isOfficeGrant = config.provider === 'officegrant';

      try {
        // Get editor launch data (token, WOPI src, server URL)
        let editorData;
        if (publicFileToken) {
          // NOTE: public editing currently only has a Collabora backend endpoint.
          const effectiveAccessCode =
            publicAccessCode || getPublicFilePassword(publicFileToken) || undefined;
          editorData = await collaboraService.getPublicEditorUrl(
            publicFileToken,
            Number(fileId),
            effectiveAccessCode,
          );
        } else if (isOnlyOffice) {
          editorData = await collaboraService.getOnlyOfficeEditorUrl(fileId);
        } else if (isOfficeGrant) {
          editorData = await collaboraService.getOfficeGrantEditorUrl(fileId);
        } else {
          editorData = await collaboraService.getEditorUrl(fileId);
        }

        if (cancelled || !mountedRef.current) return;

        let editorUrl: string | null;
        if (editorData.editorUrl) {
          // ONLYOFFICE / OfficeGrant: discovery already resolved server-side (no browser CORS).
          editorUrl = editorData.editorUrl;
        } else if (isOfficeGrant) {
          // OfficeGrant: form POST to /wopi?WOPISrc=… (see OFFICEGRANT_SPECIFICATION).
          const serverUrl = editorData.officeGrantServerUrl ?? config.officeGrantServerUrl ?? '';
          if (!serverUrl) {
            console.error('OfficeGrant: no server URL in editor launch data');
            return;
          }
          editorUrl = `${serverUrl.replace(/\/$/, '')}/wopi?WOPISrc=${encodeURIComponent(editorData.wopiSrc)}`;
        } else {
          // Collabora: resolve discovery client-side (its server sends permissive CORS).
          const serverUrl = editorData.collaboraServerUrl ?? editorData.onlyOfficeServerUrl ?? '';
          if (!serverUrl) {
            console.error('WOPI editor: no server URL in editor launch data');
            return;
          }
          const discoveryMap = await collaboraService.getDiscoveryMap(serverUrl);
          if (cancelled || !mountedRef.current) return;
          editorUrl = collaboraService.buildEditorUrl(
            discoveryMap,
            response.fileExtension,
            editorData.wopiSrc,
            isReadOnly,
          );
        }

        if (!editorUrl) {
          console.error(
            `WOPI editor: no editor URL found for extension "${response.fileExtension}"`,
          );
          return;
        }

        // OfficeGrant: bake shell prefs into the iframe URL so first paint need not wait for postMessage.
        if (isOfficeGrant) {
          const prefs = useAppStore.getState();
          editorUrl = appendAiGrantPreferencesToUrl(editorUrl, {
            locale: prefs.locale,
            colorSchemePreference: prefs.colorSchemePreference,
            highContrastEnabled: prefs.highContrastEnabled,
            reduceMotionEnabled: prefs.reduceMotionEnabled,
          });
        }

        setWopiSource(editorUrl);
        setAccessToken(editorData.accessToken);
        setAccessTokenExpiry(editorData.accessTokenTtl);
      } catch (err) {
        console.error('WOPI editor: failed to set up', err);
      }
    };

    /**
     * Main orchestration: fetch provider config, file info, then set up the right editor.
     */
    const init = async () => {
      try {
        // Save current favicon to restore on cleanup
        const appIcon: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
        const prevFavicon = appIcon?.href;

        // Fetch file info (common to both providers)
        let response: FileWithToken;
        if (publicFileToken) {
          const effectiveAccessCode =
            publicAccessCode || getPublicFilePassword(publicFileToken) || undefined;
          response = await publicFileService.getPublicFileForViewer(
            publicFileToken,
            Number(fileId),
            effectiveAccessCode,
          );
        } else {
          response = await fileService.getFileForViewer(Number(fileId), {
            acToken: tokenFromUrl,
            vid: fileVersionId ? Number(fileVersionId) : undefined,
          });
        }

        if (cancelled || !mountedRef.current) return;

        // Handle viewer response (redirects, file metadata)
        handleViewerResponse(response);

        // Check if the response indicated a redirect (file not set means redirect happened)
        if (!response.wTkn) return;

        // Beta "new editor" path: no EditorUrl call; OfficeGrant bootstraps from /open.
        if (editorChoice === 'officegrant') {
          const officeGrantBaseUrl = String(import.meta.env.VITE_APP_OFFICEGRANT_URL ?? '').trim();
          if (!officeGrantBaseUrl) {
            console.error(
              'OfficeGrant beta: VITE_APP_OFFICEGRANT_URL is required when VITE_APP_OFFICEGRANT_BETA=true; falling back to classic',
            );
          } else {
            setupOfficeGrantBetaOpen(officeGrantBaseUrl);
            cleanupFaviconRef.current = () => {
              if (appIcon && prevFavicon) appIcon.href = prevFavicon;
            };
            return;
          }
        }

        // Classic path: existing provider flow (OnlyOffice / Collabora / MS / config officegrant).
        {
          let config = officeConfig;
          if (!config) {
            config = await collaboraService.getOfficeConfig();
            if (cancelled || !mountedRef.current) return;
            setOfficeConfig(config);
          }

          const isWopiProvider =
            config.provider === 'collabora' ||
            config.provider === 'onlyoffice' ||
            config.provider === 'officegrant';

          if (isWopiProvider) {
            await setupWopiEditor(response, config);
          } else {
            setupMsOffice(response);
          }
        }

        // Store cleanup ref for favicon restoration
        cleanupFaviconRef.current = () => {
          if (appIcon && prevFavicon) appIcon.href = prevFavicon;
        };
      } catch (err) {
        console.error('Office: failed to initialize', err);
      } finally {
        if (!cancelled && mountedRef.current) setIsLoading(false);
      }
    };

    void init();

    // Cleanup ref for favicon restoration
    return () => {
      cancelled = true;
      cleanupFaviconRef.current?.();
    };
    // ponytail: searchParams object identity churned on every render — use serialized QS
  }, [type, fileId, searchParams.toString(), navigate, getPublicFilePassword, editorChoice]);

  // Ref for favicon cleanup function
  const cleanupFaviconRef = useRef<(() => void) | null>(null);

  /**
   * Once file/token info and src are ready, build iframe.
   * Only fires once the needed data is present.
   */
  useEffect(() => {
    // Must have Office token with known expiry, and WOPI src URL to proceed
    if (!accessToken || accessTokenExpiry === 0 || !wopiSource) return;
    // Only build frame if none present (prevents duplicates)
    if (document.getElementById('office_frame')) return;

    buildOfficeFrame();
  }, [accessToken, accessTokenExpiry, wopiSource, buildOfficeFrame]);

  // Location-scoped tab title (file name when known)
  useRoutePageTitle({
    isLoading,
    label: file?.fileName?.trim() || undefined,
  });

  /**
   * Monitors access token expiration and shows a non-closable modal when token expires.
   * The modal forces the user to reload the page to get a new token.
   */
  useEffect(() => {
    // OfficeGrant beta `/open` owns auth/token refresh; skip FM expiry modal.
    if (useDirectIframeSrc) return;
    // Only monitor if we have a valid expiry timestamp
    if (!accessTokenExpiry || accessTokenExpiry === 0) return;

    // Check if token is already expired
    const now = Date.now();
    if (accessTokenExpiry <= now) {
      // Token already expired, show modal immediately
      if (!tokenExpiredModalRef.current) {
        tokenExpiredModalRef.current = modal.warning({
          title: t('modal.officeTokenExpired.title'),
          content: t('modal.officeTokenExpired.description'),
          closable: false,
          mask: { closable: false },
          footer: (
            <div className="flex justify-end gap-2">
              <Button type="primary" onClick={() => window.location.reload()}>
                {t('common.reloadPage')}
              </Button>
            </div>
          ),
          centered: true,
          width: 400,
          zIndex: 9999,
          styles: { mask: { backdropFilter: 'blur(5px)' } },
          className: 'bg-black/25! backdrop-blur-sm!',
        });
      }
      return;
    }

    // Calculate time until expiration
    const timeUntilExpiry = accessTokenExpiry - now;

    // Set up a timeout to show the modal when token expires
    const timeoutId = setTimeout(() => {
      if (!mountedRef.current) return;

      if (!tokenExpiredModalRef.current) {
        tokenExpiredModalRef.current = modal.warning({
          title: t('modal.officeTokenExpired.title'),
          content: t('modal.officeTokenExpired.description'),
          closable: false,
          mask: { closable: false },
          footer: (
            <div className="flex justify-end gap-2">
              <Button type="primary" onClick={() => window.location.reload()}>
                {t('common.reloadPage')}
              </Button>
            </div>
          ),
          centered: true,
          width: 400,
          zIndex: 9999,
          styles: { mask: { backdropFilter: 'blur(5px)' } },
          className: 'bg-black/25! backdrop-blur-sm!',
        });
      }
    }, timeUntilExpiry);

    // Cleanup timeout on unmount or when expiry changes
    return () => {
      clearTimeout(timeoutId);
    };
  }, [accessTokenExpiry, useDirectIframeSrc, modal, t]);

  /**
   * Listens for WOPI / OfficeGrant PostMessage events.
   * Handles: App_LoadingStatus (Document_Loaded), UI_Close, Action_Save_Resp.
   * Includes beta `/open` (useDirectIframeSrc) — not only collabora/onlyoffice.
   */
  useEffect(() => {
    if (
      !shouldListenForOfficeEditorLifecycle({
        provider: officeConfig?.provider,
        useDirectIframeSrc,
      })
    ) {
      return;
    }

    const allowedOrigins = collectOfficeEditorLifecycleOrigins({
      onlyOfficeServerUrl: officeConfig?.onlyOfficeServerUrl,
      collaboraServerUrl: officeConfig?.collaboraServerUrl,
      officeGrantServerUrl: officeConfig?.officeGrantServerUrl,
      officeGrantEnvUrl: import.meta.env.VITE_APP_OFFICEGRANT_URL,
      iframeSrc: useDirectIframeSrc ? wopiSource : undefined,
    });
    if (allowedOrigins.length === 0) return;

    const handleMessage = (event: MessageEvent) => {
      if (!allowedOrigins.includes(event.origin)) return;

      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }

      const messageId = data?.MessageId;

      switch (messageId) {
        case 'App_LoadingStatus':
          if (data.Values?.Status === 'Document_Loaded') {
            setIsLoading(false);
          }
          break;
        case 'UI_Close':
          if (onRequestClose) {
            onRequestClose();
          } else {
            navigate(-1);
          }
          break;
        case 'Action_Save_Resp':
          // Save completed — optional feedback (currently no-op)
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [officeConfig, navigate, onRequestClose, useDirectIframeSrc, wopiSource]);

  /**
   * Syncs shell preferences with OfficeGrant iframe (locale, theme, accessibility).
   */
  useEffect(() => {
    const candidateOrigins: string[] = [];

    if (officeConfig?.officeGrantServerUrl) {
      try {
        candidateOrigins.push(new URL(officeConfig.officeGrantServerUrl).origin);
      } catch {
        /* ignore invalid URL */
      }
    }

    const configuredOfficeGrantUrl = import.meta.env.VITE_APP_OFFICEGRANT_URL;
    if (configuredOfficeGrantUrl) {
      try {
        candidateOrigins.push(new URL(configuredOfficeGrantUrl).origin);
      } catch {
        /* ignore invalid URL */
      }
    }

    if (candidateOrigins.length === 0) {
      return;
    }

    const preferenceActions = {
      setLocale,
      setColorSchemePreference,
      setHighContrastEnabled,
      setReduceMotionEnabled,
    };

    const handleMessage = (event: MessageEvent) => {
      if (!candidateOrigins.includes(event.origin)) {
        return;
      }

      let payload = event.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }

      const data = parseOfficePostMessageData(payload);
      if (!data) {
        return;
      }

      const hostPreferences = readFilemanagerHostPreferences({
        locale,
        colorSchemePreference,
        highContrastEnabled,
        reduceMotionEnabled,
      });

      switch (data.MessageId) {
        case OfficePostMessageId.AppPreferencesRequest:
          sendHostPreferencesToEditor(event.source, event.origin, hostPreferences);
          break;
        case OfficePostMessageId.AppPreferences: {
          const childPreferences = extractHostPreferencesFromMessage(data);
          if (childPreferences) {
            applyFilemanagerHostPreferences(childPreferences, preferenceActions);
          }
          break;
        }
        case OfficePostMessageId.AppRequestFileAction: {
          const request = parseRequestFileActionValues(data.Values);
          if (!request || !parsedFile || request.fileId !== parsedFile.id) {
            break;
          }
          switch (request.action) {
            case 'share':
              if (!canShareFile) {
                notification.error({ title: t('errors.forbidden') });
                break;
              }
              handleShareFiles([parsedFile]);
              break;
            case 'move':
              if (!canMoveFile || hasPublicFileToken) {
                notification.error({ title: t('errors.forbidden') });
                break;
              }
              void handleOpenMoveModal();
              break;
            case 'rename':
              handleHostRename(event.source, event.origin);
              break;
            case 'delete':
              handleHostDelete();
              break;
            case 'details':
              handleHostDetails();
              break;
            case 'download':
              if (!canDownloadFile) {
                notification.error({ title: t('errors.forbidden') });
                break;
              }
              handleDownload();
              break;
            case 'downloadEncrypted':
              // Same gate as Office FloatButtons: download permission covers encrypted too.
              if (!canDownloadFile) {
                notification.error({ title: t('errors.forbidden') });
                break;
              }
              void handleDownloadEncryptedFile(parsedFile);
              break;
          }
          break;
        }
        case OfficePostMessageId.AppOpenAccount:
          navigate('/account/profile');
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    officeConfig?.officeGrantServerUrl,
    locale,
    colorSchemePreference,
    highContrastEnabled,
    reduceMotionEnabled,
    setLocale,
    setColorSchemePreference,
    setHighContrastEnabled,
    setReduceMotionEnabled,
    parsedFile,
    canShareFile,
    canMoveFile,
    canDownloadFile,
    hasPublicFileToken,
    handleShareFiles,
    handleOpenMoveModal,
    handleHostRename,
    handleHostDelete,
    handleHostDetails,
    handleDownload,
    handleDownloadEncryptedFile,
    notification,
    t,
    navigate,
  ]);

  /**
   * Returns dropdown items for AI operations (document chat/summary),
   * locking options based on file limitations.
   */
  const aiFileActionsItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];
    if (!file || !parsedFile) return items;

    if (!hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileAIChat)) {
      items.push({
        key: 'ai-chat',
        label: t('common.artificialIntelligenceDocumentChat'),
        icon: <Icon name="BotMessageSquareIcon" className="h-5 w-5" />,
        onClick: () => {
          setRightPanelKind('aiDocumentChat');
          setRightPanelContent(<AiDocumentChat file={parsedFile} />);
          openRightPanel();
        },
      });
    }
    if (!hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileAISummary)) {
      items.push({
        key: 'ai-summary',
        label: t('common.artificialIntelligenceSummary'),
        icon: <Icon name="ScanTextIcon" className="h-5 w-5" />,
        onClick: () => {
          // Kind first: FilesHeader sync treats default 'detail' as FileDetail and overwrites.
          setRightPanelKind('aiSummary');
          setRightPanelContent(
            <AiSummary
              file={file}
              onSummaryGenerated={() => {}}
              onRequestClose={closeRightPanel}
            />,
          );
          openRightPanel();
        },
      });
    }
    return items;
  }, [
    file,
    t,
    parsedFile,
    openRightPanel,
    closeRightPanel,
    setRightPanelContent,
    setRightPanelKind,
  ]);

  // Provide states and actions to consuming component/view
  return {
    isLoading,
    isMoveActionLoading,
    file,
    accessToken,
    accessTokenExpiry,
    wopiSource,
    parsedFile,
    canShareFile,
    canMoveFile,
    canOpenContainingFolder,
    canDownloadFile,
    hasPublicFileToken,
    hideHostOwnedFloatActions,
    handleShare,
    handleDownload,
    handleOpenMoveModal,
    handleOpenContainingFolder,
    handleDownloadEncryptedFile,
    aiFileActionsItems,
    isRightPanelOpen,
  };
}
